import { useEffect } from "react";
import { useLocalStorage } from "react-use";

// Store for all registered storage items
const storageItems: Map<string, () => void> = new Map();

// Hook to handle the reset functionality once
export const useGlobalReset = () => {
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has("reset")) {
      console.log("Resetting all storage items", storageItems);
      // Reset all registered storage items
      storageItems.forEach((remove, key) => {
        remove();
        localStorage.removeItem(key);
      });

      // Remove the reset parameter
      window.history.replaceState({}, document.title, window.location.pathname);

      // Reload to ensure clean state
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => window.location.reload());
      } else {
        setTimeout(() => window.location.reload(), 0);
      }
    }
  }, []);
};

type UseLocalStorageParams<T> = Parameters<typeof useLocalStorage<T>>;
type UseLocalStorageReturn<T> = ReturnType<typeof useLocalStorage<T>>;

export default function useResetableStorage<T>(
  storageKey: string,
  initialState?: T,
  options?: UseLocalStorageParams<T>[2],
): UseLocalStorageReturn<T> {
  const [data, setData, remove] = useLocalStorage<T>(
    storageKey,
    initialState,
    options,
  );

  useEffect(() => {
    storageItems.set(storageKey, remove);

    return () => {
      storageItems.delete(storageKey);
    };
  }, [storageKey, remove]);

  return [data, setData, remove] as const;
}
